import { Suspense, useEffect, useRef } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import "./App.css";

import { GoToAppStore } from './pages/auth/GoToAppStore';
import { ReferralRedirect } from './pages/auth/ReferralRedirect';
import { RegisterScreen } from './pages/auth/RegisterScreen';
import { DrawAHeart } from './pages/onboarding/DrawAHeart';
import { HighFive } from './pages/onboarding/HighFive';
import { OcdCycleExplain } from './pages/onboarding/OcdCycleExplain';
import { OcdCycleIntro } from './pages/onboarding/OcdCycleIntro';
import { OcdThemesDetailsSelect } from './pages/onboarding/OcdThemesDetailsSelect';
import { OcdThemesSelection } from './pages/onboarding/OcdThemesSelection';
import { OnboardingRating } from './pages/onboarding/OnboardingRating';
import { PersonalizingOllie } from './pages/onboarding/PersonalizingOllie';
import PaywallPage from './pages/paywall/PaywallPage';

import { Capacitor } from '@capacitor/core';

import { OnBoardingLayout } from './pages/onboarding/OnBoardingLayout';
import { WhatBringsYou } from './pages/onboarding/WhatBringsYou';
import { LoadingScreen } from "./shared/components/LoadingScreen";
import { OfflineScreen } from "./shared/components/OfflineScreen";
import { TikTokBrowserScreen } from "./shared/components/TikTokBrowserScreen";
import { AppStatus, useAppBootstrap } from "./shared/hooks/useAppBootstrap";
import { useGeoRedirect } from "./shared/hooks/useGeoRedirect";
import { useScreenTracking } from "./shared/hooks/useScreenTracking";
import { useTikTokBrowserDetection } from "./shared/hooks/useTikTokBrowserDetection";
import { ROUTES } from './shared/routing/routes';

function App() {
  const { isCheckingGeo, isRedirecting } = useGeoRedirect();
  const { appStatus, entryRoute } = useAppBootstrap();
  useScreenTracking();
  const isTikTokBrowser = useTikTokBrowserDetection();
  const navigate = useNavigate();
  const location = useLocation();
  const hasPerformedInitialRedirect = useRef(false);

  useEffect(() => {
    if (
      appStatus === AppStatus.Ready &&
      entryRoute !== undefined &&
      !hasPerformedInitialRedirect.current
    ) {
      hasPerformedInitialRedirect.current = true;

      if (location.pathname !== entryRoute) {
        navigate(entryRoute, { replace: true });
      }
    }
  }, [appStatus, entryRoute, location.pathname, navigate]);

  if (isCheckingGeo || isRedirecting) {
    return null;
  }

  if (appStatus === AppStatus.Loading || entryRoute === undefined) {
    return null;
  }

  // Early return for TikTok browser detection
  if (isTikTokBrowser) {
    return <TikTokBrowserScreen />;
  }

  // Early return only for offline status
  if (appStatus === AppStatus.Offline) {
    return <OfflineScreen />;
  }

  return (
    <div className={`App max-w-main mx-auto no-select${!Capacitor.isNativePlatform() ? ' is-web' : ''}`}>
      <Suspense fallback={<LoadingScreen />}>
        <Routes>

          {/* Root route for new users */}
          <Route path={ROUTES.root} element={<OnBoardingLayout />}>
            <Route index element={<WhatBringsYou />} />
          </Route>

          {/* Onboarding routes - must come before /:referralCode */}
          <Route path={ROUTES.onboarding.index} element={<OnBoardingLayout />}>
            <Route path={ROUTES.onboarding.whatBringsYou} element={<WhatBringsYou />} />
            <Route path={ROUTES.onboarding.drawHeart} element={<DrawAHeart />} />
            <Route path={ROUTES.onboarding.ocdThemesSelection} element={<OcdThemesSelection />} />
            <Route path={ROUTES.onboarding.ocdThemesDetailsSelect} element={<OcdThemesDetailsSelect />} />
            <Route path={ROUTES.onboarding.ocdCycleIntro} element={<OcdCycleIntro />} />
            <Route path={ROUTES.onboarding.ocdCycleExplain} element={<OcdCycleExplain />} />
            <Route path={ROUTES.onboarding.personalizingOllie} element={<PersonalizingOllie />} />
            <Route path={ROUTES.onboarding.register} element={<RegisterScreen />} />
            <Route path={ROUTES.onboarding.highFive} element={<HighFive />} />
            <Route path={ROUTES.onboarding.rating} element={<OnboardingRating />} />
          </Route>

          <Route
            path={ROUTES.onboarding.paywall}
            element={<PaywallPage />}
          />
          <Route
            path={ROUTES.onboarding.goToAppStore}
            element={<GoToAppStore />}
          />

          {/* Referral code catch-all for any single-segment path like /CODE */}
          <Route path={ROUTES.referral} element={<ReferralRedirect entryRoute={entryRoute} />} />

          {/* Final catch-all for unknown routes */}
          <Route
            path="*"
            element={<Navigate replace to={entryRoute ?? ROUTES.onboarding.whatBringsYou} />
            }
          />
        </Routes>
      </Suspense>
    </div>
  );
}

export default App;
